import { type ImgHTMLAttributes } from 'react';

type ImageSize = {
  width: number;
  height: number;
  quality?: number;
};

type ImageSizeWithViewport = ImageSize & {
  breakpoint: number;
};

interface KontentImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'src' | 'alt'> {
  sizes: Readonly<[ImageSize, ...ImageSizeWithViewport[]]>;
  src: string;
  alt: string;
}

const generateSrcSet = (src: string, size: ImageSize) => {
  return [1, 2]
    .map(
      (scale) =>
        `${src}?w=${size.width}&h=${size.height}&dpr=${scale}&fit=crop&q=${size.quality || 70} ${scale}x`
    )
    .join(', ');
};

const KontentImage = ({
  sizes,
  src,
  alt,
  decoding = 'async',
  loading = 'lazy',
  ...rest
}: KontentImageProps) => {
  const [defaultSize, ...restSizes] = sizes;
  return (
    <picture>
      {restSizes
        .toSorted((a, b) => b.breakpoint - a.breakpoint)
        .map((size) => {
          const key = `${size.width}-${size.height}-${size.quality}-${size.breakpoint}`;
          return (
            <source
              key={key}
              media={`(min-width: ${size.breakpoint}px)`}
              srcSet={generateSrcSet(src, size)}
              width={size.width}
              height={size.height}
            />
          );
        })}
      <source
        srcSet={generateSrcSet(src, defaultSize)}
        width={defaultSize.width}
        height={defaultSize.height}
      />
      <img
        src={`${src}?w=${defaultSize.width}&h=${defaultSize.height}&fit=crop&q=${defaultSize.quality || 70}`}
        {...rest}
        alt={alt}
        decoding={decoding}
        loading={loading}
        width={defaultSize.width}
        height={defaultSize.height}
      />
    </picture>
  );
};

export default KontentImage;
